import { useEffect, useState } from "react";
import { Bus, CableCar, Clock3, MapPin, Plane, Radio, Ship, TrainFront } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { fetchLines } from "../lib/api";
import { dictionaries } from "../lib/i18n";
import type { NearbyStatus, NearbyStop, StopLine, UiLang } from "../lib/types";

interface Props {
  stopId: string;
  lang: UiLang;
}

/**
 * Ikonet per transportmåte. Entur-modus som nøkkel.
 *
 * Åtte modi er alt som forekommer, og det er *målt*: en folketelling over 3 859
 * holdeplasser ved de 118 stoppene ga bus, rail, tram, water, metro, air,
 * funicular og cableway. De øvrige seks i Enturs enum — lift, trolleybus,
 * monorail, taxi, coach, unknown — ble aldri sett på nabo-nivå, men `taxi` og
 * `coach` forekommer på *linje*-nivå og har derfor egne oppføringer her.
 *
 * `funicular` og `cableway` var den viktige mangelen: Fløyens fire nærmeste
 * holdeplasser er alle `funicular` — Fløibanen, Bergens mest besøkte
 * attraksjon — så uten et eget ikon rendret hele den synlige lista som busser.
 *
 * `ferry` er slettet. Den finnes ikke i Enturs v3-enum (ferjer er `water` med
 * en undermodus) og forekom i ingen av ~4 900 linjeoppføringer.
 */
const MODE_ICON: Record<string, LucideIcon> = {
  rail: TrainFront,
  metro: TrainFront,
  tram: TrainFront,
  bus: Bus,
  coach: Bus,
  water: Ship,
  funicular: CableCar,
  cableway: CableCar,
  air: Plane,
  taxi: Bus,
};

/**
 * Hvor mange linjer som vises før resten legges bak en knapp.
 *
 * Oslo S har over førti. De ti hyppigste er svaret på «hva går herfra»; resten
 * er en liste man skroller forbi.
 */
const VISIBLE = 10;

/** Hvor langt ut nabolista går, i kilometer luftlinje. Speiler services/lines.ts. */
const NEARBY_KM = 3;

/**
 * Hva som faktisk går gjennom stoppet, og hvor ofte.
 *
 * Tidslinja sier «tog 14:22 til Bergen» — ett forslag, den avgangen planen
 * bygger på. Det er ikke det man lurer på når man vurderer å legge inn en
 * avstikker til Flåm: da vil man vite hva som går forbi, hvor ofte, og når det
 * siste går.
 *
 * Forskjellen er avgjørende på disse rutene. «Hver time» og «fire ganger om
 * dagen» er to helt forskjellige reiser, og fire avganger daglig betyr at et
 * bommet tog koster deg dagen. Det er verdt å vite før man planlegger.
 *
 * Hentes når stoppet åpnes, ikke sammen med planen: ett Entur-kall per stopp i
 * en femdagers plan ville spist store deler av kvoten på underforespørsler, for
 * noe man leser om ett stopp av gangen.
 */
export function LinesPanel({ stopId, lang }: Props) {
  const t = dictionaries[lang];
  const [lines, setLines] = useState<StopLine[] | null>(null);
  const [nearby, setNearby] = useState<NearbyStop[]>([]);
  const [nearbyStatus, setNearbyStatus] = useState<NearbyStatus>("ok");
  const [failed, setFailed] = useState(false);
  const [all, setAll] = useState(false);

  useEffect(() => {

    /*
     * `cancelled` framfor AbortController: svaret er mellomlagret på serveren i
     * en time, så et avbrutt kall sparer ingenting — men et svar som kommer
     * etter at brukeren har åpnet et annet stopp må ikke skrives inn i den nye
     * visningen. Det ville vist Bergens avganger under Voss.
     */
    let cancelled = false;
    setLines(null);
    setFailed(false);
    setAll(false);

    fetchLines(stopId, lang)
      .then((body) => {
        if (cancelled) return;
        setLines(body.lines);
        setNearby(body.nearby ?? []);
        setNearbyStatus(body.nearbyStatus ?? "unavailable");
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [stopId, lang]);

  if (failed) return <p className="lines-empty">{t.linesFailed}</p>;
  if (lines === null) return <p className="lines-empty">{t.linesLoading}</p>;

  const shown = all ? lines : lines.slice(0, VISIBLE);

  return (
    <div className="lines">
      {/*
        Ingen avganger *herfra* er ikke det samme som ingenting i nærheten.
        41 stopp har ingen holdeplass av sitt eget — hele Jotunheimen — og for
        dem er nabolista under hele svaret.
      */}
      {lines.length === 0 && <p className="lines-empty">{t.linesNone}</p>}
      <ul className="line-list">
        {shown.map((line) => {
          const Icon = MODE_ICON[line.mode] ?? Bus;
          return (
            <li className="line" key={`${line.line}-${line.destination}`}>
              <span className={`line-code line-code--${modeClass(line.mode)}`}>
                <Icon size={11} strokeWidth={2.4} />
                {line.line}
              </span>

              <span className="line-where">
                <b>{line.destination || line.name}</b>
                {line.authority && <small>{line.authority}</small>}
              </span>

              <span className="line-often">
                {line.everyMinutes ? (
                  <b>
                    <Clock3 size={11} />
                    {t.linesEvery(line.everyMinutes)}
                  </b>
                ) : (
                  <b>{t.linesPerDay(line.perDay, line.atLeast ?? false)}</b>
                )}
                {/*
                  De neste avgangstidene under intervallet. Intervallet sier om
                  det er verdt å planlegge rundt; klokkeslettene sier om man
                  rekker det i dag.
                */}
                <small>{line.departures.slice(0, 3).map(clock).join(" · ")}</small>
              </span>

              {line.realtime && (
                <span className="line-live" title={t.linesRealtime}>
                  <Radio size={10} />
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {/*
        Holdeplassene i nærheten.
        
        Svarer på et annet spørsmål enn lista over: den sier hva som går *forbi
        dette stoppet*, denne sier hva som finnes *rundt det*. Avstanden er
        luftlinje og er også det lista er sortert på — Enturs egen avstand er
        gangavstand, og de to er genuint uenige (Stegastein: 968 m i luftlinje,
        8 717 m å gå).
      */}
      <div className="nearby">
        <h4 className="nearby-title">
          <MapPin size={12} />
          {t.nearbyTitle(NEARBY_KM)}
        </h4>

        {nearbyStatus === "off-network" ? (
          /*
           * «Utenfor gangnettet» er ikke «ingen holdeplass i nærheten».
           * Svartisen, Nigardsbreen, Hallingskarvet, Dovrefjell og Lysefjord
           * svarer LOCATION_NOT_FOUND uansett radius — vi vet ikke veien dit,
           * og å påstå at det ikke finnes noe er en påstand om stedet.
           */
          <p className="lines-empty">{t.nearbyOffNetwork}</p>
        ) : nearbyStatus === "unavailable" ? (
          <p className="lines-empty">{t.nearbyUnavailable}</p>
        ) : nearby.length === 0 ? (
          <p className="lines-empty">{t.nearbyNone(NEARBY_KM)}</p>
        ) : (
          <>
            <ul className="nearby-list">
              {nearby.map((place) => {
                const Icon = MODE_ICON[place.modes[0]] ?? Bus;
                return (
                  <li className="nearby-stop" key={place.id}>
                    <span className={`line-code line-code--${modeClass(place.modes[0] ?? "")}`}>
                      <Icon size={11} strokeWidth={2.4} />
                    </span>
                    <span className="nearby-name">{place.name}</span>
                    <span className="nearby-km">{t.nearbyAway(place.distanceKm)}</span>
                  </li>
                );
              })}
            </ul>
            {/*
              Panelet lover ikke rutegående trafikk. Målt: 13,5 % av radene har
              ingen avganger i det neste døgnet, og de dukker opp i byene også —
              Tromsø har sin første på plass tre av 75. Å slå opp hver av dem
              ville kostet åtte ekstra Entur-kall per klikk, mot et tak på
              femti. «Registrert nå» og aldri «ute av drift»: en 60-dagers
              prøve ga null for dem, men Entur har ingen ruteplan et år fram, så
              en sommerkai og en nedlagt er ikke til å skille fra hverandre.
            */}
            <p className="nearby-note">{t.nearbyNote}</p>
          </>
        )}
      </div>

      {lines.length > VISIBLE && (
        <button type="button" className="ghost-button lines-more" onClick={() => setAll(!all)}>
          {all ? t.linesFewer : t.linesMore(lines.length - VISIBLE)}
        </button>
      )}

      <p className="lines-credit">{t.linesCredit}</p>
    </div>
  );
}

/** Klasse per transportmåte, så fargen skiller tog fra buss. */
/**
 * Fargeklassen per transportmåte.
 *
 * `taxi` og `coach` har egne farger fordi de er *ekte og allerede i drift*, og
 * feil før dette: linje «8290 Folldal–Hjerkinn» er en bestillingstaxi med fem
 * avganger i det viste døgnet, og den rendret som en buss i bussfarge.
 * Sognefjordekspressen og åtte andre ekspressbusser er `coach`.
 *
 * Taubane og kabelbane deler «lift». Reserven er fortsatt buss, men nå bare
 * for de modiene som aldri ble observert.
 */
function modeClass(mode: string): string {
  if (mode === "rail" || mode === "metro" || mode === "tram") return "rail";
  if (mode === "water") return "boat";
  if (mode === "funicular" || mode === "cableway") return "lift";
  if (mode === "taxi") return "taxi";
  if (mode === "coach") return "coach";
  return "bus";
}

/**
 * Klokkeslettet fra en ISO-tid, i lokal tid.
 *
 * Entur svarer med tidssone, og norsk sommertid er +02:00. `slice(11, 16)` på
 * strengen ville vist riktig tall her og feil tall for en bruker i en annen
 * sone, så tiden tolkes og formateres.
 */
function clock(iso: string): string {
  const stamp = new Date(iso);
  if (Number.isNaN(stamp.getTime())) return "";
  return stamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
